import {
  trackIdentityStepStarted,
  trackIdentityStepLeft,
} from '@/composables/tracking/useConversionTracking';

// Routes that live inside identity.js but are not user-facing form steps.
// `identity` is the shell redirector; `identity.dashboard` is largely deprecated
// and treated as non-step for tracking purposes.
const NON_STEP_ROUTES = new Set(['identity', 'identity.dashboard']);

const isIdentityStep = (route) => Boolean(
  route?.meta?.identityStep && route?.name && !NON_STEP_ROUTES.has(route.name),
);

/**
 * Builds a tracking payload that includes any route params (e.g. `/identity/idUpload/:id`),
 * so parametric steps remain distinguishable in the timeline.
 */
const paramsExtra = (route) => {
  const params = route?.params;
  if (!params || typeof params !== 'object') return {};
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  return entries.length ? { step_params: Object.fromEntries(entries) } : {};
};

/**
 * Emit IDENTITY_STEP_LEFT / IDENTITY_STEP_STARTED for transitions involving
 * any route in `src/router/identity.js` (flagged via `meta.identityStep`).
 *
 * Safe to call on every navigation; it no-ops when neither side is an identity step.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {import('vue-router').RouteLocationNormalized} from
 */
export const trackIdentityStepTransition = (to, from) => {
  if (!to || !from || to.name === from.name) return;

  const fromIsStep = isIdentityStep(from);
  const toIsStep = isIdentityStep(to);

  if (fromIsStep) {
    trackIdentityStepLeft({
      step_type: from.name,
      to_step: to.name ?? null,
      to_is_identity: toIsStep,
      ...paramsExtra(from),
    });
  }

  if (toIsStep) {
    trackIdentityStepStarted({
      step_type: to.name,
      from_step: from.name ?? null,
      from_is_identity: fromIsStep,
      ...paramsExtra(to),
    });
  }
};

export default {
  trackIdentityStepTransition,
};
